import React from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PricesPage = ({ language }) => {
  const translations = {
    de: {
      title: 'Preise',
      subtitle: 'Transparente und faire Preisgestaltung für Ihre IT-Bedürfnisse',
      CustomerPrice: {
        title: 'Stundensatz für Privatkunden',
        price: '60€',
        unit: 'pro Stunde',
        description: 'Für individuelle Projekte und Beratungsleistungen',
        cta: 'Anfrage senden'
      },
      BusinessPrice: {
        title: 'Stundensatz für Unternehmen',
        price: '80€',
        unit: 'pro Stunde',
        description: 'Für individuelle Projekte und Beratungsleistungen',
        cta: 'Anfrage senden'
      },
      packages: {
        title: 'Dienstleistungspakete',
        description: 'Übersicht häufig benötigter Services - Preise auf Anfrage',
        items: [
          {
            title: 'Windows 11 Umstieg',
            features: [
              'Komplette Installation',
              'Datenübertragung',
              'Systemeinrichtung',
              'Benutzer-Schulung'
            ]
          },
          {
            title: 'PC/Laptop Einrichtung',
            features: [
              'Kaufberatung',
              'Ersteinrichtung',
              'Software-Installation',
              'Grundkonfiguration'
            ]
          },
          {
            title: 'Netzwerk-Setup',
            features: [
              'Router-Konfiguration',
              'WLAN-Optimierung',
              'Sicherheitseinrichtung',
              'Dokumentation'
            ]
          },
          {
            title: 'Microsoft 365 Setup',
            features: [
              'Account-Einrichtung',
              'E-Mail-Konfiguration',
              'Cloud-Integration',
              'Benutzer-Training'
            ]
          },
          {
            title: 'Datensicherung & Cloud',
            features: [
              'Backup-Lösung',
              'Cloud-Anbindung',
              'Automatisierung',
              'Wiederherstellungstest'
            ]
          },
          {
            title: 'Fernwartung (monatlich)',
            features: [
              'Regelmäßige Updates',
              'Systemüberwachung',
              'Schnelle Problemlösung',
              'Monatlicher Report'
            ]
          }
        ]
      },
      note: {
        title: 'Hinweis',
        text: 'Preise inklusive Anfahrt innerhalb 10 km vom Geschäftssitz (Burglengenfeld). Darüber hinaus zzgl. 0,75 €/km. Kleinunternehmerregelung § 19 UStG – keine USt. ausgewiesen.'
      },
      contact: {
        title: 'Individuelle Anfrage?',
        description: 'Kontaktieren Sie mich für ein maßgeschneidertes Angebot',
        button: 'Jetzt anfragen'
      }
    },
    en: {
      title: 'Prices',
      subtitle: 'Transparent and fair pricing for your IT needs',
      CustomerPrice: {
        title: 'Hourly Rate for private Customers',
        price: '60€',
        unit: 'per hour',
        description: 'For custom projects and consulting services',
        cta: 'Send inquiry'
      },
      BusinessPrice: {
        title: 'Hourly Rate for Business Customers',
        price: '80€',
        unit: 'per hour',
        description: 'For custom projects and consulting services',
        cta: 'Send inquiry'
      },
      packages: {
        title: 'Service Packages',
        description: 'Overview of frequently needed services - Prices available on request',
        items: [
          {
            title: 'Windows 11 Migration',
            features: [
              'Complete installation',
              'Data transfer',
              'System setup',
              'User training'
            ]
          },
          {
            title: 'PC/Laptop Setup',
            features: [
              'Purchase consultation',
              'Initial setup',
              'Software installation',
              'Basic configuration'
            ]
          },
          {
            title: 'Network Setup',
            features: [
              'Router configuration',
              'WiFi optimization',
              'Security setup',
              'Documentation'
            ]
          },
          {
            title: 'Microsoft 365 Setup',
            features: [
              'Account setup',
              'Email configuration',
              'Cloud integration',
              'User training'
            ]
          },
          {
            title: 'Backup & Cloud',
            features: [
              'Backup solution',
              'Cloud connection',
              'Automation',
              'Recovery test'
            ]
          },
          {
            title: 'Remote Maintenance (monthly)',
            features: [
              'Regular updates',
              'System monitoring',
              'Fast problem solving',
              'Monthly report'
            ]
          }
        ]
      },
      note: {
        title: 'Note',
        text: 'Travel costs within 10 km radius of our Burglengenfeld office are included in all prices. Additional distance charged at €0.75/km. Operating under § 19 UStG small business exemption – VAT not itemized.'
      },
      contact: {
        title: 'Individual Inquiry?',
        description: 'Contact me for a customized quote',
        button: 'Inquire now'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" style={{ color: '#E53D4A' }}>
            {t.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            {t.subtitle}
          </p>
        </div>

        {/* Customer Price */}
        <div className="max-w-2xl mx-auto mb-16">
          <Card className="text-center border-2 border-[#E53D4A]">
            <CardHeader>
              <CardTitle className="text-3xl" style={{ color: '#E53D4A' }}>
                {t.CustomerPrice.title}
              </CardTitle>
              <div className="py-6">
                <span className="text-5xl font-bold" style={{ color: '#E53D4A' }}>
                  {t.CustomerPrice.price}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">
                  {t.CustomerPrice.unit}
                </span>
              </div>
              <CardDescription className="text-base">
                {t.CustomerPrice.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="bg-[#E53D4A] hover:bg-[#d63340]" asChild>
                <a href="mailto:kontakt@martinez.systems">
                  {t.CustomerPrice.cta}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Business Price */}
        <div className="max-w-2xl mx-auto mb-16">
          <Card className="text-center border-2 border-[#E53D4A]">
            <CardHeader>
              <CardTitle className="text-3xl" style={{ color: '#E53D4A' }}>
                {t.BusinessPrice.title}
              </CardTitle>
              <div className="py-6">
                <span className="text-5xl font-bold" style={{ color: '#E53D4A' }}>
                  {t.BusinessPrice.price}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">
                  {t.BusinessPrice.unit}
                </span>
              </div>
              <CardDescription className="text-base">
                {t.BusinessPrice.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="bg-[#E53D4A] hover:bg-[#d63340]" asChild>
                <a href="mailto:kontakt@martinez.systems">
                  {t.BusinessPrice.cta}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Service Packages */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#E53D4A' }}>
              {t.packages.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.packages.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {t.packages.items.map((item, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle style={{ color: '#E53D4A' }}>
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {item.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Note */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="text-center border-2 border-[#E53D4A]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: '#E53D4A' }}>
                {t.note.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400">
                {t.note.text}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <Card className="max-w-2xl mx-auto bg-[#E53D4A] text-white border-none">
            <CardHeader>
              <CardTitle className="text-2xl text-white">
                {t.contact.title}
              </CardTitle>
              <CardDescription className="text-gray-200">
                {t.contact.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="bg-white text-[#E53D4A] hover:bg-gray-100" asChild>
                <a href="mailto:kontakt@martinez.systems">
                  {t.contact.button}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PricesPage;
